import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Stack,
  Divider,
  Alert,
  Chip,
  IconButton,
  InputAdornment,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Slider,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  alpha
} from '@mui/material';
import { usePermissions } from '../../../utils/permissions';
import {
  AttachMoney as CostIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Calculate as CalculateIcon,
  TrendingUp as TrendingUpIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as SupplierIcon,
  Factory as FactoryIcon,
  Business as BusinessIcon,
  Info as InfoIcon,
  Assessment as AssessmentIcon,
  Remove as RemoveIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { supabase } from '../../../supabaseClient';
import {
  convertUnits,
  getUnitType,
  calculateRawMaterialCost,
  WEIGHT_UNITS,
  VOLUME_UNITS
} from '../../../utils/unitConversions';

const BatchCosts = ({
  batch,
  rawMaterials,
  formulations,
  showSnackbar,
  userCompanyId,
  onUpdate,
  user
}) => {
  const theme = useTheme();
  const { canAccessPage, canEditPage } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [ingredients, setIngredients] = useState([]);
  const [formulationIngredients, setFormulationIngredients] = useState([]);
  const [saving, setSaving] = useState(false);
  
  // Cost parameters (like CostsTab)
  const [laborPercentage, setLaborPercentage] = useState(40);
  
  // Global overhead percentage
  const [globalOverheadPercentage, setGlobalOverheadPercentage] = useState(28); // Total overhead %
  
  // Overhead categories as % of the overhead budget (should sum to 100%)
  const [utilitiesPercentage, setUtilitiesPercentage] = useState(10);        
  const [facilityPercentage, setFacilityPercentage] = useState(25);          
  const [equipmentPercentage, setEquipmentPercentage] = useState(15);        
  const [adminLaborPercentage, setAdminLaborPercentage] = useState(18);      
  const [warehousePercentage, setWarehousePercentage] = useState(7);         
  const [qualityPercentage, setQualityPercentage] = useState(10);            
  const [softwarePercentage, setSoftwarePercentage] = useState(8);           
  const [insurancePercentage, setInsurancePercentage] = useState(7);         
  
  // Additional costs
  const [shippingCost, setShippingCost] = useState(0);
  const [wasteCost, setWasteCost] = useState(0);
  const [reworkCost, setReworkCost] = useState(0);
  const [miscCost, setMiscCost] = useState(0);
  
  // Settings
  const [editMode, setEditMode] = useState(false);
  const [costsInputted, setCostsInputted] = useState(false);
  const [displayUnit, setDisplayUnit] = useState('kg');
  
  // Calculated costs (like CostsTab structure)
  const [costs, setCosts] = useState({
    totalIngredientCost: 0,
    laborCost: 0,
    overheadCost: 0,
    additionalCost: 0,
    totalCost: 0,
    costPerUnit: 0,
    ingredientCosts: []
  });

  // Accordion state management with localStorage persistence
  const [accordionStates, setAccordionStates] = useState({
    costSummary: false,
    rawMaterialBreakdown: false,
    laborCost: false,
    overheadCosts: false,
    additionalCosts: false,
    phases: {} // Dynamic object for phase accordions
  });

  // Load accordion states from localStorage on mount
  useEffect(() => {
    const savedStates = localStorage.getItem(`batchCosts_accordions_${batch?.id || 'default'}`);
    if (savedStates) {
      try {
        setAccordionStates(JSON.parse(savedStates));
      } catch (error) {
        console.warn('Failed to parse saved accordion states:', error);
      }
    }
  }, [batch?.id]);

  // Save accordion states to localStorage when they change
  const updateAccordionState = (accordionKey, expanded) => {
    const newStates = { ...accordionStates, [accordionKey]: expanded };
    setAccordionStates(newStates);
    localStorage.setItem(`batchCosts_accordions_${batch?.id || 'default'}`, JSON.stringify(newStates));
  };

  // Update phase accordion state
  const updatePhaseAccordionState = (phaseId, expanded) => {
    const newStates = { 
      ...accordionStates, 
      phases: { ...(accordionStates.phases || {}), [phaseId]: expanded }
    };
    setAccordionStates(newStates);
    localStorage.setItem(`batchCosts_accordions_${batch?.id || 'default'}`, JSON.stringify(newStates));
  };

  const allUnits = [...WEIGHT_UNITS, ...VOLUME_UNITS];
  
  // Calculate distribution total (should add up to 100% for proper distribution)
  const distributionTotal = utilitiesPercentage + facilityPercentage + equipmentPercentage + 
                           adminLaborPercentage + warehousePercentage + qualityPercentage + 
                           softwarePercentage + insurancePercentage;

  // Format currency (same as CostsTab)
  const formatCurrency = (amount) => `$${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  
  // Format cost per unit with more decimal places for small amounts
  const formatCostPerUnit = (amount) => {
    if (!amount || amount === 0) return '$0.00';
    if (amount < 0.01) {
      return `$${amount.toFixed(4)}`;
    }
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Calculate raw material conversion amounts (same logic as IngredientsTab and BatchIngredients)
  const calculateRawMaterialAmountNeeded = (amount, formulationUnit, rawMaterial) => {
    if (!rawMaterial || !amount) return null;
    
    const materialDensity = rawMaterial.density_g_ml || 1.0;
    const rawMaterialUnit = rawMaterial.primary_unit_measure;
    
    // If same units, no conversion needed
    if (!rawMaterialUnit || rawMaterialUnit === formulationUnit) {
      return null;
    }

    try {
      // Convert formulation amount to raw material units using unitConversions.js with density
      const rawMaterialAmountNeeded = convertUnits(
        amount,
        formulationUnit,
        rawMaterialUnit,
        materialDensity
      );

      return {
        amount: rawMaterialAmountNeeded.toFixed(3),
        unit: rawMaterialUnit,
        density: materialDensity.toFixed(3)
      };
    } catch (error) {
      console.warn(`Raw material unit conversion failed for ${rawMaterial?.name}: ${error.message}`);
      return null;
    }
  };

  useEffect(() => {
    if (batch?.id) {
      loadBatchIngredients();
      loadFormulationIngredients();
      loadBatchCosts();
    }
  }, [batch?.id]);

  useEffect(() => {
    // Set display unit to formulation unit if available, otherwise batch unit
    const formulation = formulations.find(f => f.id === batch?.formulation_id);
    const formulationUnit = formulation?.unit_of_measure;
    
    if (formulationUnit) {
      setDisplayUnit(formulationUnit);
    } else if (batch?.planned_unit_of_measure) {
      setDisplayUnit(batch.planned_unit_of_measure);
    }
  }, [batch?.formulation_id, batch?.planned_unit_of_measure, formulations]);

  useEffect(() => {
    calculateIngredientCosts();
  }, [ingredients, formulationIngredients, rawMaterials, laborPercentage, globalOverheadPercentage,
      utilitiesPercentage, facilityPercentage, equipmentPercentage, adminLaborPercentage,
      warehousePercentage, qualityPercentage, softwarePercentage, insurancePercentage,
      shippingCost, wasteCost, reworkCost, miscCost, displayUnit]);

  const loadBatchIngredients = async () => {
    try {
      const { data, error } = await supabase
        .from('batch_ingredients')
        .select(`
          *,
          raw_materials:raw_material_id (
            id, name, sku, unit_cost, primary_unit_measure,
            primary_unit_quantity, primary_unit_type, density_g_ml
          ),
          formulation_phases:formulation_phase_id (
            id, name, sequence
          )
        `)
        .eq('batch_id', batch.id);

      if (error) throw error;
      setIngredients(data || []);
    } catch (error) {
      console.error('Error loading batch ingredients:', error);
    }
  };

  const loadFormulationIngredients = async () => {
    if (!batch.formulation_id) return;
    
    try {
      const { data, error } = await supabase
        .from('formulation_ingredients_new')
        .select(`
          *,
          raw_materials:ingredient_id (
            id, name, sku, unit_cost, primary_unit_measure,
            primary_unit_quantity, primary_unit_type, density_g_ml
          ),
          formulation_phases:formulation_phase_id (
            id, name, sequence
          )
        `)
        .eq('formulation_id', batch.formulation_id)
        .order('sequence');

      if (error) throw error;
      setFormulationIngredients(data || []);
    } catch (error) {
      console.error('Error loading formulation ingredients:', error);
    }
  };

  const loadBatchCosts = async () => {
    try {
      const { data, error } = await supabase
        .from('batch_costs')
        .select('*')
        .eq('batch_id', batch.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      
      if (data) {
        setLaborPercentage(data.labor_percentage ?? 40);
        setGlobalOverheadPercentage(data.global_overhead_percentage ?? 28);
        setUtilitiesPercentage(data.utilities_percentage ?? 10);
        setFacilityPercentage(data.facility_percentage ?? 25);
        setEquipmentPercentage(data.equipment_percentage ?? 15);
        setAdminLaborPercentage(data.admin_labor_percentage ?? 18);
        setWarehousePercentage(data.warehouse_percentage ?? 7);
        setQualityPercentage(data.quality_percentage ?? 10);
        setSoftwarePercentage(data.software_percentage ?? 8);
        setInsurancePercentage(data.insurance_percentage ?? 7);
        setShippingCost(data.shipping_cost ?? 0);
        setWasteCost(data.waste_cost ?? 0);
        setReworkCost(data.rework_cost ?? 0);
        setMiscCost(data.misc_cost ?? 0);
        setCostsInputted(true);
      }
    } catch (error) {
      console.error('Error loading batch costs:', error);
    }
  };

  // Calculate weighted average density (same logic as CostsTab)
  const calculateFormulationDensity = () => {
    if (!formulationIngredients.length || !rawMaterials.length) {
      return 1.0; // Default cosmetic density
    }
    
    let totalWeightedDensity = 0;
    let totalPercentage = 0;
    
    formulationIngredients.forEach(ingredient => {
      const rawMaterial = rawMaterials.find(rm => rm.id === ingredient.ingredient_id);
      const percentage = ingredient.percentage || 0;
      const materialDensity = rawMaterial?.density_g_ml;
      
      if (materialDensity && materialDensity > 0 && percentage > 0) {
        totalWeightedDensity += (materialDensity * percentage);
        totalPercentage += percentage;
      }
    });
    
    if (totalPercentage > 0) {
      return totalWeightedDensity / totalPercentage;
    }
    
    return 1.0;
  };

  // Calculate ingredient costs (adapted from CostsTab logic)
  const calculateIngredientCosts = () => {
    if (!ingredients.length && !formulationIngredients.length) return;

    // Use formulation ingredients if batch ingredients aren't generated yet
    const sourceIngredients = ingredients.length > 0 ? ingredients : formulationIngredients;
    const isUsingFormulation = ingredients.length === 0;

    // Calculate ingredient costs
    const ingredientCosts = sourceIngredients.map(ingredient => {
      const rawMaterial = isUsingFormulation ? 
        rawMaterials.find(rm => rm.id === ingredient.ingredient_id) :
        ingredient.raw_materials;

      if (!rawMaterial || !rawMaterial.unit_cost) {
        return {
          ingredient_id: isUsingFormulation ? ingredient.ingredient_id : ingredient.raw_material_id,
          ingredient_name: rawMaterial?.name || 'Unknown',
          total_cost: 0,
          has_pricing: false,
          amount: 0,
          percentage: isUsingFormulation ? ingredient.percentage : ingredient.planned_percentage
        };
      }

      let amount = 0;
      let unit = 'kg'; // Default fallback

      if (isUsingFormulation) {
        // Calculate from formulation percentage using proper scaling logic
        const formulation = formulations.find(f => f.id === batch.formulation_id);
        const formulationBatchSize = formulation?.target_batch_size || 1000;
        const formulationUnit = formulation?.unit_of_measure || 'kg';
        const targetBatchSize = batch.planned_quantity || 100;
        const targetUnit = batch.planned_unit_of_measure || 'oz';
        
        // Calculate scaling factor using same logic as BatchOverview/BatchIngredients
        let scalingFactor = 1;
        
        try {
          if (targetUnit !== formulationUnit) {
            const targetUnitType = getUnitType(targetUnit);
            const formulationUnitType = getUnitType(formulationUnit);
            
            if (targetUnitType !== formulationUnitType) {
              // Different unit types - use density (same logic as other components)
              const calculatedDensity = calculateFormulationDensity();
              
              if (formulationUnitType === 'volume' && targetUnitType === 'weight') {
                const formulationInML = convertUnits(formulationBatchSize, formulationUnit, 'ml');
                const formulationInGrams = formulationInML * calculatedDensity;
                const formulationInTargetUnits = convertUnits(formulationInGrams, 'g', targetUnit);
                scalingFactor = targetBatchSize / formulationInTargetUnits;
              } else if (formulationUnitType === 'weight' && targetUnitType === 'volume') {
                const formulationInGrams = convertUnits(formulationBatchSize, formulationUnit, 'g');
                const formulationInML = formulationInGrams / calculatedDensity;
                const formulationInTargetUnits = convertUnits(formulationInML, 'ml', targetUnit);
                scalingFactor = targetBatchSize / formulationInTargetUnits;
              }
            } else {
              // Same unit types - direct conversion
              const targetInFormulationUnits = convertUnits(targetBatchSize, targetUnit, formulationUnit);
              scalingFactor = targetInFormulationUnits / formulationBatchSize;
            }
          } else {
            scalingFactor = targetBatchSize / formulationBatchSize;
          }
        } catch (error) {
          console.warn('Unit conversion failed for cost calculation, using fallback:', error);
          scalingFactor = targetBatchSize / formulationBatchSize;
        }
        
        // Calculate scaled amount in formulation units
        const baseAmount = (ingredient.percentage / 100) * formulationBatchSize;
        amount = baseAmount * scalingFactor;
        unit = formulationUnit; // Use formulation unit for cost calculations
      } else {
        // Use actual or planned amount from batch
        amount = ingredient.actual_amount || ingredient.planned_amount || 0;
        unit = ingredient.actual_unit || ingredient.planned_unit || 'kg';
      }

      const costData = calculateRawMaterialCost(amount, unit, rawMaterial);

      return {
        ingredient_id: isUsingFormulation ? ingredient.ingredient_id : ingredient.raw_material_id,
        ingredient_name: rawMaterial.name,
        total_cost: costData.totalCost,
        has_pricing: true,
        amount: amount,
        unit: unit,
        percentage: isUsingFormulation ? ingredient.percentage : ingredient.planned_percentage,
        phase_id: isUsingFormulation ? ingredient.formulation_phase_id : ingredient.formulation_phase_id,
        phase_name: isUsingFormulation ? ingredient.formulation_phases?.name : ingredient.formulation_phases?.name,
        phase_sequence: isUsingFormulation ? ingredient.formulation_phases?.sequence : ingredient.formulation_phases?.sequence,
      };
    });

    const totalIngredientCost = ingredientCosts.reduce((sum, item) => sum + (item.total_cost || 0), 0);
    const laborCost = totalIngredientCost * (laborPercentage / 100);
    
    // Calculate overhead as percentage of raw materials only
    const overheadCost = totalIngredientCost * (globalOverheadPercentage / 100);

    const additionalCost = shippingCost + wasteCost + reworkCost + miscCost;
    const totalCost = totalIngredientCost + laborCost + overheadCost + additionalCost;
    
    // Calculate cost per formulation unit AND cost per product unit
    let costPerFormulationUnit = 0;
    let costPerProductUnit = 0;
    
    const formulation = formulations.find(f => f.id === batch?.formulation_id);
    const formulationBatchSize = formulation?.target_batch_size || 1000;
    const formulationUnit = formulation?.unit_of_measure || 'kg';
    const totalProductUnits = batch?.units_to_make || 2772;
    
    // Calculate proper scaling factor (same logic as other components)
    let scalingFactor = 1;
    const batchQuantity = batch?.planned_quantity || 0;
    const batchUnit = batch?.planned_unit_of_measure || 'oz';
    
    try {
      if (batchUnit !== formulationUnit) {
        const batchUnitType = getUnitType(batchUnit);
        const formulationUnitType = getUnitType(formulationUnit);
        
        if (batchUnitType !== formulationUnitType) {
          // Different unit types - use density
          const calculatedDensity = calculateFormulationDensity();
          
          if (formulationUnitType === 'weight' && batchUnitType === 'weight') {
            // Both weight - direct conversion
            const formulationInBatchUnits = convertUnits(formulationBatchSize, formulationUnit, batchUnit);
            scalingFactor = batchQuantity / formulationInBatchUnits;
          } else if (formulationUnitType === 'volume' && batchUnitType === 'weight') {
            const formulationInBatchUnits = convertUnits(formulationBatchSize, formulationUnit, batchUnit, calculatedDensity);
            scalingFactor = batchQuantity / formulationInBatchUnits;
          } else if (formulationUnitType === 'weight' && batchUnitType === 'volume') {
            const formulationInBatchUnits = convertUnits(formulationBatchSize, formulationUnit, batchUnit, calculatedDensity);
            scalingFactor = batchQuantity / formulationInBatchUnits;
          }
        } else {
          // Same unit types - direct conversion
          const formulationInBatchUnits = convertUnits(formulationBatchSize, formulationUnit, batchUnit);
          scalingFactor = batchQuantity / formulationInBatchUnits;
        }
      } else {
        scalingFactor = batchQuantity / formulationBatchSize;
      }
    } catch (error) {
      console.warn('Failed to calculate scaling factor for costs:', error);
      scalingFactor = batchQuantity / formulationBatchSize;
    }
    
    const scaledFormulationSize = formulationBatchSize * scalingFactor;
    
    // Cost per formulation unit (kg)
    if (scaledFormulationSize > 0 && totalCost > 0) {
      costPerFormulationUnit = totalCost / scaledFormulationSize;
    }
    
    // Cost per product unit (16 oz bottle)
    if (totalProductUnits > 0 && totalCost > 0) {
      costPerProductUnit = totalCost / totalProductUnits;
    }

    setCosts({
      totalIngredientCost,
      laborCost,
      overheadCost,
      additionalCost,
      totalCost,
      costPerUnit: costPerFormulationUnit, // Cost per kg
      costPerProductUnit, // Cost per 16 oz unit
      ingredientCosts
    });
  };

  // Calculate actual production volume (same logic as BatchOverview)
  const calculateActualProductionVolume = () => {
    if (!batch || !formulationIngredients.length) {
      return null;
    }
    
    const targetSize = batch.planned_quantity || 0;
    const targetUnit = batch.planned_unit_of_measure || 'oz';
    
    // Calculate weighted average density from formulation ingredients (same as batch analysis)
    let calculatedDensity = 1.0; // Default cosmetic density
    
    if (formulationIngredients.length > 0) {
      let totalWeightedDensity = 0;
      let totalPercentage = 0;
      
      formulationIngredients.forEach(ingredient => {
        const rawMaterial = ingredient.raw_materials;
        const percentage = ingredient.percentage || 0;
        const materialDensity = rawMaterial?.density_g_ml;
        
        if (materialDensity && materialDensity > 0 && percentage > 0) {
          totalWeightedDensity += (materialDensity * percentage);
          totalPercentage += percentage;
        }
      });
      
      if (totalPercentage > 0) {
        calculatedDensity = totalWeightedDensity / totalPercentage;
      }
    }
    
    // Convert to gallons for display
    try {
      const targetUnitType = getUnitType(targetUnit);
      
      if (targetUnitType === 'weight') {
        // Convert weight to volume using unitConversions.js with density
        const targetInGallons = convertUnits(targetSize, targetUnit, 'gal', calculatedDensity);
        
        return {
          volume: targetInGallons,
          unit: 'gal',
          density: calculatedDensity,
          originalSize: targetSize,
          originalUnit: targetUnit
        };
      } else {
        // Already volume - convert directly to gallons
        const targetInGallons = convertUnits(targetSize, targetUnit, 'gal');
        
        return {
          volume: targetInGallons,
          unit: 'gal',
          density: calculatedDensity,
          originalSize: targetSize,
          originalUnit: targetUnit
        };
      }
    } catch (error) {
      console.warn('Volume conversion failed:', error);
      return null;
    }
  };

  const saveCosts = async () => {
    setSaving(true);
    try {
      // Check if batch costs already exist
      const { data: existingCosts } = await supabase
        .from('batch_costs')
        .select('id')
        .eq('batch_id', batch.id)
        .single();

      const costDataToSave = {
        batch_id: batch.id,
        labor_percentage: laborPercentage,
        global_overhead_percentage: globalOverheadPercentage,
        utilities_percentage: utilitiesPercentage,
        facility_percentage: facilityPercentage,
        equipment_percentage: equipmentPercentage,
        admin_labor_percentage: adminLaborPercentage,
        warehouse_percentage: warehousePercentage,
        quality_percentage: qualityPercentage,
        software_percentage: softwarePercentage,
        insurance_percentage: insurancePercentage,
        shipping_cost: shippingCost,
        waste_cost: wasteCost,
        rework_cost: reworkCost,
        misc_cost: miscCost,
        total_ingredient_cost: costs.totalIngredientCost,
        labor_cost: costs.laborCost,
        overhead_cost: costs.overheadCost,
        additional_cost: costs.additionalCost,
        total_cost: costs.totalCost,
        cost_per_unit: costs.costPerUnit,
        total_production_batch_cost: costs.totalCost,
        calculated_overhead_cost: costs.overheadCost,
        updated_by: user?.id
      };

      if (existingCosts) {
        // Update existing record
        const { error } = await supabase
          .from('batch_costs')
          .update(costDataToSave)
          .eq('id', existingCosts.id);
        
        if (error) throw error;
      } else {
        // Insert new record
        const { error } = await supabase
          .from('batch_costs')
          .insert([{
            ...costDataToSave,
            created_by: user?.id
          }]);
        
        if (error) throw error;
      }

      setCostsInputted(true);
      setEditMode(false);
      showSnackbar('Batch costs saved successfully!', 'success');
      onUpdate();
    } catch (error) {
      console.error('Error saving batch costs:', error);
      showSnackbar('Error saving batch costs: ' + error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const getVarianceColor = (variance) => {
    if (variance > 5) return 'error';
    if (variance > 0) return 'warning';
    return 'success';
  };

  const hasNoPricing = costs.ingredientCosts?.some(item => !item.has_pricing);

  // Permission check for batch costs
  if (!canAccessPage('batch_costs')) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '70vh',
        textAlign: 'center',
        px: 3
      }}>
        <Card sx={{
          p: 8,
          borderRadius: 3,
          border: `1px solid ${theme.palette.grey[400]}`,
          background: 'white',
          boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
          maxWidth: 600,
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          cursor: 'default',
          transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
            opacity: 0.8,
            transition: 'opacity 0.3s ease'
          },
          '&:hover::before': {
            opacity: 1
          }
        }}>
          <Box sx={{ 
            p: 3, 
            borderRadius: 3, 
            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            mb: 6,
            display: 'inline-flex',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
          }}>
            <CostIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h3" sx={{ 
            mb: 3, 
            fontWeight: 500, 
            color: 'text.primary',
            fontSize: '2.5rem',
            letterSpacing: '-0.8px'
          }}>
            Access Restricted
          </Typography>
          <Typography variant="h6" sx={{ 
            color: 'text.secondary', 
            mb: 4,
            fontWeight: 500,
            fontSize: '1.2rem'
          }}>
            Batch Costs Permissions Required
          </Typography>
          <Typography variant="body1" sx={{ 
            color: 'text.secondary',
            lineHeight: 1.7,
            fontSize: '1.05rem',
            maxWidth: 450,
            mx: 'auto'
          }}>
            You don't currently have the necessary permissions to access the Batch Costs. 
            Contact your system administrator to request access or verify your role permissions.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: 2,
      pl: 3,
      pr: 3,
      maxWidth: '98vw',
      width: '100%'
    }}>
      <Grid container spacing={3}>
        {/* Panel A: Cost Overview */}
        <Grid item xs={12} lg={3.5}>
          <Card sx={{ 
            height: 'fit-content',
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: `white`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Stack spacing={3}>
                {/* Header */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 2, 
                      background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      backdropFilter: 'blur(10px)'
                    }}>
                      <CostIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                    </Box>
                    <Box sx={{ ml: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                        Cost Analysis
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Production costs
                      </Typography>
                    </Box>
                  </Box>

                  <Stack direction="row" spacing={2}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={() => {
                        loadBatchIngredients();
                        loadFormulationIngredients();
                        loadBatchCosts();
                      }}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1.5,
                        color: theme.palette.primary.main,
                        fontWeight: 500,
                        background: theme.palette.grey[400],
                        border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                        backdropFilter: 'blur(10px)',
                        '&:hover': {
                          backgroundColor: theme.palette.grey[400]
                        }
                      }}
                    >
                      Refresh
                    </Button>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                      onClick={saveCosts}
                      disabled={saving || !canEditPage('batch_costs')}
                      sx={{
                        borderRadius: 2,
                        px: 4,
                        py: 1.5,
                        background: canEditPage('batch_costs') ? 
                          `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})` :
                          theme.palette.grey[400],
                        color: canEditPage('batch_costs') ? 'white' : theme.palette.grey[100],
                        fontWeight: 500,
                        boxShadow: canEditPage('batch_costs') ? 
                          `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
                        '&:hover': canEditPage('batch_costs') ? {
                          background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                          boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                        } : {},
                        '&:disabled': {
                          background: theme.palette.grey[400],
                          color: theme.palette.grey[100]
                        }
                      }}
                    >
                      Save
                    </Button>
                  </Stack>
                </Box>

                {/* Total Cost Display */}
                <Accordion 
                  expanded={accordionStates.costSummary}
                  onChange={(event, expanded) => updateAccordionState('costSummary', expanded)}
                  elevation={0}
                  sx={{
                    borderRadius: '12px !important',
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    backdropFilter: 'blur(20px)',
                    overflow: 'hidden',
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                    '&:before': {
                      display: 'none'
                    },
                    '&.Mui-expanded': {
                      margin: '16px 0 16px 0',
                      borderRadius: '12px !important'
                    },
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '& .MuiAccordion-region': {
                      borderRadius: '0 0 12px 12px !important'
                    }
                  }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                    sx={{
                      background: 'white',
                      backdropFilter: 'blur(10px)',
                      '& .MuiAccordionSummary-content': { alignItems: 'center', my: 2 },
                      '&:hover': {
                        background: alpha(theme.palette.primary.main, 0.04)
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <AssessmentIcon sx={{ color: theme.palette.primary.main }} />
                      <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                        Cost Summary
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      <Box sx={{ 
                        p: 2,
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                        backdropFilter: 'blur(10px)',
                        textAlign: 'center',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                        }
                      }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          TOTAL COST
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 500, color: '#000000' }}>
                          {formatCurrency(costs.totalCost)}
                        </Typography>
                      </Box>

                      <Box sx={{ 
                        p: 2,
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                        backdropFilter: 'blur(10px)',
                        textAlign: 'center',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                        }
                      }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          COST PER {(() => {
                            const formulation = formulations.find(f => f.id === batch?.formulation_id);
                            return (formulation?.unit_of_measure || 'kg').toUpperCase();
                          })()}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 500, color: '#000000' }}>
                          {formatCostPerUnit(costs.costPerUnit)}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ 
                        p: 2,
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                        backdropFilter: 'blur(10px)',
                        textAlign: 'center',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                        }
                      }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          COST PER PRODUCT UNIT
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 500, color: '#000000' }}>
                          {formatCostPerUnit(costs.costPerProductUnit)}
                        </Typography>
                      </Box>
                    </Stack>
                  </AccordionDetails>
                </Accordion>


                {/* Alerts */}
                {hasNoPricing && (
                  <Alert 
                    severity="warning" 
                    sx={{ 
                      fontSize: '0.8rem',
                      borderRadius: 2,
                      border: '1px solid #ff9800',
                      fontWeight: 500
                    }}
                  >
                    Some ingredients missing pricing data
                  </Alert>
                )}

                {!costsInputted && (
                  <Alert 
                    severity="info" 
                    sx={{ 
                      fontSize: '0.8rem',
                      borderRadius: 2,
                      border: '1px solid #2196f3',
                      fontWeight: 500
                    }}
                  >
                    Configure cost parameters to get accurate analysis
                  </Alert>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Panel B: Cost Configuration */}
        <Grid item xs={12} lg={5.5}>
          <Card sx={{ 
            height: 'fit-content',
            borderRadius: 4,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: `white`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 3 }}>
                Cost Configuration
              </Typography>

              <Stack spacing={2}>
                {/* Raw Material Costs Breakdown */}
                {costs.ingredientCosts.length > 0 && (
                  <Accordion 
                    expanded={accordionStates.rawMaterialBreakdown}
                    onChange={(event, expanded) => updateAccordionState('rawMaterialBreakdown', expanded)}
                    elevation={0}
                    sx={{
                      borderRadius: '12px !important',
                      border: `1px solid ${theme.palette.grey[400]}`,
                      background: `linear-gradient(${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                      backdropFilter: 'blur(20px)',
                      overflow: 'hidden',
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                      '&:before': {
                        display: 'none'
                      },
                      '&.Mui-expanded': {
                        margin: '8px 0 8px 0',
                        borderRadius: '12px !important'
                      },
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '& .MuiAccordion-region': {
                        borderRadius: '0 0 12px 12px !important'
                      }
                    }}
                  >
                    <AccordionSummary 
                      expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                      sx={{
                        background: 'white',
                        backdropFilter: 'blur(10px)',
                        minHeight: 56,
                        '& .MuiAccordionSummary-content': { alignItems: 'center', my: 2 },
                        '&:hover': {
                          background: alpha(theme.palette.primary.main, 0.04)
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                        <InfoIcon sx={{ color: theme.palette.primary.main }} />
                        <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                          Ingredients Breakdown
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                          {formatCurrency(costs.totalIngredientCost)}
                        </Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2 }}>
                      <Stack spacing={2}>
                        {/* Material Cost Summary */}
                        <Box sx={{ 
                          p: 2,
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                          backdropFilter: 'blur(10px)',
                          mb: 2,
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                          }
                        }}>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={4}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Total Ingredients
                              </Typography>
                              <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000' }}>
                                {formatCurrency(costs.totalIngredientCost)}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Per Unit Cost
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500, color: '#000000' }}>
                                {formatCostPerUnit(costs.totalIngredientCost / (batch?.units_to_make || 1))}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Ingredients
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500, color: '#000000' }}>
                                {costs.ingredientCosts.length}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>

                        {/* Ingredient Breakdown by Phase */}
                        <Stack spacing={2}>
                          {(() => {
                            // Group ingredients by phase
                            const phaseGroups = {};
                            
            costs.ingredientCosts.forEach(item => {
              // Create logical phases based on ingredient names if no phase assigned
              let phaseId = item.phase_id || 'no-phase';
              let phaseName = item.phase_name || 'Unassigned Phase';
              let phaseSequence = item.phase_sequence || 999;
              
                              
                              if (!phaseGroups[phaseId]) {
                                phaseGroups[phaseId] = {
                                  name: phaseName,
                                  sequence: phaseSequence,
                                  ingredients: [],
                                  totalCost: 0
                                };
                              }
                              phaseGroups[phaseId].ingredients.push(item);
                              phaseGroups[phaseId].totalCost += item.total_cost || 0;
                            });
                            
                            // Sort phases by sequence
                            const sortedPhases = Object.entries(phaseGroups).sort(
                              ([, a], [, b]) => a.sequence - b.sequence
                            );
                            
                            return sortedPhases.map(([phaseId, phase]) => (
                              <Accordion 
                                key={phaseId}
                                expanded={accordionStates.phases?.[phaseId] || false}
                                onChange={(event, expanded) => updatePhaseAccordionState(phaseId, expanded)}
                                elevation={0}
                                sx={{
                                  borderRadius: '12px !important',
                                  border: `1px solid ${theme.palette.grey[400]}`,
                                  background: `linear-gradient(${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
                                  backdropFilter: 'blur(20px)',
                                  overflow: 'hidden',
                                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                  '&:before': {
                                    display: 'none'
                                  },
                                  '&.Mui-expanded': {
                                    margin: '8px 0 8px 0',
                                    borderRadius: '12px !important'
                                  },
                                  '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                                  },
                                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                  '& .MuiAccordion-region': {
                                    borderRadius: '0 0 12px 12px !important'
                                  }
                                }}
                              >
                                <AccordionSummary 
                                  expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                                  sx={{
                                    background: 'white',
                                    backdropFilter: 'blur(10px)',
                                    minHeight: 56,
                                    '& .MuiAccordionSummary-content': { alignItems: 'center', my: 2 },
                                    '&:hover': {
                                      background: alpha(theme.palette.primary.main, 0.04)
                                    },
                                    transition: 'all 0.2s ease'
                                  }}
                                >
                                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }}>
                                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                                      {phase.name}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                                      {formatCurrency(phase.totalCost)} ({((phase.totalCost / costs.totalIngredientCost) * 100).toFixed(1)}%)
                                    </Typography>
                                  </Stack>
                                </AccordionSummary>
                                <AccordionDetails sx={{ p: 2 }}>
                                  <Stack spacing={1}>
                                    {phase.ingredients
                                      .sort((a, b) => b.total_cost - a.total_cost)
                                      .map((item, index) => {
                                        const percentageOfTotalCost = costs.totalIngredientCost > 0 ? 
                                          ((item.total_cost / costs.totalIngredientCost) * 100) : 0;
                                        
                                        return (
                                          <Box key={item.ingredient_id} sx={{ 
                                            p: 3,
                                            borderRadius: 3,
                                            border: `1px solid ${theme.palette.grey[400]}`,
                                            background: `white`,
                                            backdropFilter: 'blur(20px)',
                                            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '&:hover': {
                                              transform: 'translateY(-2px)',
                                              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                                            }
                                          }}>
                                            <Grid container spacing={2} alignItems="center">
                                              <Grid item xs={4}>
                                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>
                                                  {item.ingredient_name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                  {item.percentage?.toFixed(1)}% of formulation
                                                </Typography>
                                              </Grid>
                                              
                                              <Grid item xs={3}>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                  Amount Used
                                                </Typography>
                                                {(() => {
                                                  const sourceIngredient = ingredients.length > 0 ? 
                                                    ingredients.find(ing => ing.raw_material_id === item.ingredient_id) :
                                                    formulationIngredients.find(ing => ing.ingredient_id === item.ingredient_id);
                                                  
                                                  const rawMaterial = sourceIngredient?.raw_materials || 
                                                    rawMaterials.find(rm => rm.id === item.ingredient_id);
                                                  
                                                  const rawMaterialConversion = calculateRawMaterialAmountNeeded(
                                                    item.amount,
                                                    item.unit,
                                                    rawMaterial
                                                  );
                                                  
                                                  // Check if item.amount is already in raw material units
                                                  const isAlreadyInRawUnits = item.unit === rawMaterial?.primary_unit_measure;
                                                  
                                                  if (isAlreadyInRawUnits && rawMaterial?.primary_unit_measure) {
                                                    // Amount is already in raw material units
                                                    const formulation = formulations.find(f => f.id === batch?.formulation_id);
                                                    const formulationUnit = formulation?.unit_of_measure || 'kg';
                                                    
                                                    // Only show conversion if raw material unit is different from formulation unit
                                                    if (item.unit !== formulationUnit) {
                                                      try {
                                                        const formulationEquivalent = convertUnits(
                                                          item.amount,
                                                          item.unit,
                                                          formulationUnit,
                                                          rawMaterial.density_g_ml || 1.0
                                                        );
                                                        return (
                                                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {item.amount?.toFixed(3)} {item.unit} (≈{formulationEquivalent.toFixed(3)} {formulationUnit})
                                                          </Typography>
                                                        );
                                                      } catch (error) {
                                                        return (
                                                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {item.amount?.toFixed(2)} {item.unit}
                                                          </Typography>
                                                        );
                                                      }
                                                    } else {
                                                      // Same units - no conversion needed
                                                      return (
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                          {item.amount?.toFixed(3)} {item.unit}
                                                        </Typography>
                                                      );
                                                    }
                                                  } else if (rawMaterialConversion) {
                                                    // Show raw material amount with formulation equivalent in parentheses
                                                    return (
                                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {rawMaterialConversion.amount} {rawMaterialConversion.unit} (≈{item.amount?.toFixed(3)} {item.unit})
                                                      </Typography>
                                                    );
                                                  } else {
                                                    // Show formulation amount only
                                                    return (
                                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {item.amount?.toFixed(2)} {item.unit}
                                                      </Typography>
                                                    );
                                                  }
                                                })()}
                                              </Grid>
                                              
                                              <Grid item xs={2.5}>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                  Cost
                                                </Typography>
                                                <Typography variant="body2" sx={{ 
                                                  fontWeight: 500,
                                                  color: item.has_pricing ? '#2e7d32' : '#ff9800'
                                                }}>
                                                  {item.has_pricing ? formatCurrency(item.total_cost) : 'No Price'}
                                                </Typography>
                                              </Grid>
                                              
                                              <Grid item xs={2.5}>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                  % of Ingredients
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>
                                                  {percentageOfTotalCost.toFixed(1)}%
                                                </Typography>
                                              </Grid>
                                            </Grid>

                                            {!item.has_pricing && (
                                              <Box sx={{ 
                                                mt: 1,
                                                p: 1,
                                                borderRadius: 3,
                                                border: `1px solid ${theme.palette.grey[400]}`,
                                                background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                                                backdropFilter: 'blur(10px)'
                                              }}>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                  <WarningIcon sx={{ color: '#ff9800', fontSize: 16 }} />
                                                  <Typography variant="caption" color="#e65100" sx={{ fontWeight: 500 }}>
                                                    Missing pricing data - cost calculation incomplete
                                                  </Typography>
                                                </Stack>
                                              </Box>
                                            )}
                                          </Box>
                                        );
                                      })}
                                  </Stack>
                                </AccordionDetails>
                              </Accordion>
                            ));
                          })()}
                        </Stack>

                        {/* Summary Stats */}
                        <Box sx={{ 
                          p: 2,
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                          backdropFilter: 'blur(10px)',
                          mt: 2,
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                          }
                        }}>
                          <Grid container spacing={2}>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Priced Ingredients
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>
                                {costs.ingredientCosts.filter(item => item.has_pricing).length} / {costs.ingredientCosts.length}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Avg Cost per Unit
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>
                                {formatCostPerUnit(costs.totalIngredientCost / (batch?.units_to_make || 1))}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Total Batch Ingredients
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>
                                {formatCurrency(costs.totalIngredientCost)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Labor Cost */}
                <Accordion 
                  expanded={accordionStates.laborCost}
                  onChange={(event, expanded) => updateAccordionState('laborCost', expanded)}
                  elevation={0}
                  sx={{
                    borderRadius: '12px !important',
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    backdropFilter: 'blur(20px)',
                    overflow: 'hidden',
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                    '&:before': {
                      display: 'none'
                    },
                    '&.Mui-expanded': {
                      margin: '8px 0 8px 0',
                      borderRadius: '12px !important'
                    },
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '& .MuiAccordion-region': {
                      borderRadius: '0 0 12px 12px !important'
                    }
                  }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                    sx={{
                      background: 'white',
                      backdropFilter: 'blur(10px)',
                      minHeight: 56,
                      '& .MuiAccordionSummary-content': { alignItems: 'center', my: 2 },
                      '&:hover': {
                        background: alpha(theme.palette.primary.main, 0.04)
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                      <TrendingUpIcon sx={{ color: theme.palette.primary.main }} />
                      <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                        Labor Cost
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                        {formatCurrency(costs.laborCost)}
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                          Labor as % of material costs
                        </Typography>
                        <Slider
                          value={laborPercentage}
                          onChange={(e, newValue) => canEditPage('batch_costs') && setLaborPercentage(newValue)}
                          valueLabelDisplay="auto"
                          step={5}
                          min={0}
                          max={100}
                          disabled={!canEditPage('batch_costs')}
                          marks={[
                            { value: 0, label: '0%' },
                            { value: 50, label: '50%' },
                            { value: 100, label: '100%' }
                          ]}
                          sx={{
                            '& .MuiSlider-thumb': {
                              backgroundColor: canEditPage('batch_costs') ? theme.palette.primary.main : theme.palette.grey[400]
                            },
                            '& .MuiSlider-track': {
                              backgroundColor: canEditPage('batch_costs') ? theme.palette.primary.main : theme.palette.grey[400]
                            },
                            '& .MuiSlider-markLabel': {
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              color: canEditPage('batch_costs') ? 'text.primary' : theme.palette.grey[500],
                              '&[data-index="0"]': {
                                transform: 'translateX(0%)'
                              },
                              '&[data-index="2"]': {
                                transform: 'translateX(-100%)'
                              }
                            },
                            '&.Mui-disabled': {
                              '& .MuiSlider-thumb': {
                                backgroundColor: theme.palette.grey[400]
                              },
                              '& .MuiSlider-track': {
                                backgroundColor: theme.palette.grey[400]
                              }
                            }
                          }}
                        />
                      </Box>
                      <TextField
                        size="small"
                        label="Labor Percentage"
                        type="number"
                        value={laborPercentage}
                        onChange={(e) => canEditPage('batch_costs') && setLaborPercentage(Math.min(Math.max(parseInt(e.target.value) || 0, 0), 100))}
                        disabled={!canEditPage('batch_costs')}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { 
                              borderColor: canEditPage('batch_costs') ? alpha(theme.palette.grey[400], 0.8) : theme.palette.grey[300], 
                              borderWidth: 1,
                              borderRadius: 2
                            },
                            '&:hover fieldset': { 
                              borderColor: canEditPage('batch_costs') ? alpha(theme.palette.primary.main, 0.4) : theme.palette.grey[300], 
                              borderWidth: 1 
                            },
                            '&.Mui-focused fieldset': { 
                              borderColor: canEditPage('batch_costs') ? theme.palette.primary.main : theme.palette.grey[300], 
                              borderWidth: 1 
                            },
                            '&.Mui-disabled': {
                              '& fieldset': {
                                borderColor: theme.palette.grey[300]
                              }
                            }
                          }
                        }}
                      />
                    </Stack>
                  </AccordionDetails>
                </Accordion>

                {/* Overhead Costs */}
                <Accordion 
                  expanded={accordionStates.overheadCosts}
                  onChange={(event, expanded) => updateAccordionState('overheadCosts', expanded)}
                  elevation={0}
                  sx={{
                    borderRadius: '12px !important',
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    backdropFilter: 'blur(20px)',
                    overflow: 'hidden',
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                    '&:before': {
                      display: 'none'
                    },
                    '&.Mui-expanded': {
                      margin: '8px 0 8px 0',
                      borderRadius: '12px !important'
                    },
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '& .MuiAccordion-region': {
                      borderRadius: '0 0 12px 12px !important'
                    }
                  }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                    sx={{
                      background: 'white',
                      backdropFilter: 'blur(10px)',
                      minHeight: 56,
                      '& .MuiAccordionSummary-content': { alignItems: 'center', my: 2 },
                      '&:hover': {
                        background: alpha(theme.palette.primary.main, 0.04)
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                      <BusinessIcon sx={{ color: theme.palette.primary.main }} />
                      <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                        Overhead Costs
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                        {formatCurrency(costs.overheadCost)} ({globalOverheadPercentage.toFixed(1)}%)
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 2 }}>
                    {/* Global Overhead Percentage */}
                    <Box sx={{ 
                      mb: 3, 
                      p: 2, 
                      borderRadius: 3,
                      border: `1px solid ${theme.palette.grey[400]}`,
                      background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                      }
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 2, color: '#000000' }}>
                        Total Overhead as % of (Material + Labor) Costs
                      </Typography>
                      <Box sx={{ px: 1, mb: 2 }}>
                        <Slider
                          value={globalOverheadPercentage}
                          onChange={(e, newValue) => canEditPage('batch_costs') && setGlobalOverheadPercentage(newValue)}
                          valueLabelDisplay="auto"
                          step={1}
                          min={10}
                          max={60}
                          disabled={!canEditPage('batch_costs')}
                          marks={[
                            { value: 10, label: '10%' },
                            { value: 30, label: '30%' },
                            { value: 50, label: '50%' },
                            { value: 60, label: '60%' }
                          ]}
                          sx={{
                            '& .MuiSlider-thumb': {
                              backgroundColor: canEditPage('batch_costs') ? theme.palette.primary.main : theme.palette.grey[400]
                            },
                            '& .MuiSlider-track': {
                              backgroundColor: canEditPage('batch_costs') ? theme.palette.primary.main : theme.palette.grey[400]
                            },
                            '& .MuiSlider-markLabel': {
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              color: canEditPage('batch_costs') ? 'text.primary' : theme.palette.grey[500],
                              '&[data-index="0"]': {
                                transform: 'translateX(0%)'
                              },
                              '&[data-index="3"]': {
                                transform: 'translateX(-100%)'
                              }
                            },
                            '&.Mui-disabled': {
                              '& .MuiSlider-thumb': {
                                backgroundColor: theme.palette.grey[400]
                              },
                              '& .MuiSlider-track': {
                                backgroundColor: theme.palette.grey[400]
                              }
                            }
                          }}
                        />
                      </Box>
                      <TextField
                        size="small"
                        label="Global Overhead Percentage"
                        type="number"
                        value={globalOverheadPercentage}
                        onChange={(e) => {
                          if (!canEditPage('batch_costs')) return;
                          const val = parseFloat(e.target.value);
                          if (!isNaN(val) && val >= 10 && val <= 60) {
                            setGlobalOverheadPercentage(val);
                          } else if (e.target.value === '') {
                            setGlobalOverheadPercentage(10);
                          }
                        }}
                        disabled={!canEditPage('batch_costs')}
                        inputProps={{ min: 10, max: 60, step: 1 }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>
                        }}
                        sx={{ 
                          maxWidth: 200,
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { 
                              borderColor: canEditPage('batch_costs') ? alpha(theme.palette.grey[400], 0.8) : theme.palette.grey[300], 
                              borderWidth: 1,
                              borderRadius: 2
                            },
                            '&:hover fieldset': { 
                              borderColor: canEditPage('batch_costs') ? alpha(theme.palette.primary.main, 0.4) : theme.palette.grey[300], 
                              borderWidth: 1 
                            },
                            '&.Mui-focused fieldset': { 
                              borderColor: canEditPage('batch_costs') ? theme.palette.primary.main : theme.palette.grey[300], 
                              borderWidth: 1 
                            },
                            '&.Mui-disabled': {
                              '& fieldset': {
                                borderColor: theme.palette.grey[300]
                              }
                            }
                          }
                        }}
                      />
                    </Box>
                    
                                        {/* Enhanced Sticky Summary */}
                    <Box sx={{ 
                      p: 2,
                      borderRadius: 3,
                      border: `1px solid ${distributionTotal > 100 ? '#f44336' : theme.palette.grey[400]}`,
                      background: distributionTotal > 100 ? 
                        `linear-gradient(${alpha('#f44336', 0.05)}, ${alpha('#f44336', 0.02)})` :
                        `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                      backdropFilter: 'blur(10px)',
                      mb: 2,
                      position: 'sticky',
                      top: 10,
                      zIndex: 10,
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                      }
                    }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={3}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Global Overhead
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, color: '#000000' }}>
                            {globalOverheadPercentage}%
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={3}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Distribution Used
                          </Typography>
                          <Typography variant="body1" sx={{ 
                            fontWeight: 500, 
                            color: distributionTotal > 100 ? '#f44336' : '#000000' 
                          }}>
                            {distributionTotal}%
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={3}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Overhead Cost
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, color: '#000000' }}>
                            {formatCurrency(costs.overheadCost)}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={3}>
                          {distributionTotal > 100 ? (
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              onClick={() => {
                                setUtilitiesPercentage(10);
                                setFacilityPercentage(25);
                                setEquipmentPercentage(15);
                                setAdminLaborPercentage(18);
                                setWarehousePercentage(7);
                                setQualityPercentage(10);
                                setSoftwarePercentage(8);
                                setInsurancePercentage(7);
                              }}
                              sx={{ 
                                fontSize: '0.75rem', 
                                py: 0.5, 
                                px: 1,
                                borderRadius: 2,
                                fontWeight: 500
                              }}
                            >
                              Fix Distribution
                            </Button>
                          ) : (
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Status
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#2e7d32' }}>
                                ✓ Valid
                              </Typography>
                            </Box>
                          )}
                        </Grid>
                      </Grid>
                    </Box>
                    
                    {/* Simple List Layout */}
                    <Stack spacing={1}>
                      {[
                        { label: 'Utilities', value: utilitiesPercentage, setter: setUtilitiesPercentage, color: '#2196f3' },
                        { label: 'Facility', value: facilityPercentage, setter: setFacilityPercentage, color: '#4caf50' },
                        { label: 'Equipment', value: equipmentPercentage, setter: setEquipmentPercentage, color: '#ff9800' },
                        { label: 'Admin Labor', value: adminLaborPercentage, setter: setAdminLaborPercentage, color: '#9c27b0' },
                        { label: 'Warehouse', value: warehousePercentage, setter: setWarehousePercentage, color: '#607d8b' },
                        { label: 'Quality', value: qualityPercentage, setter: setQualityPercentage, color: '#e91e63' },
                        { label: 'Software', value: softwarePercentage, setter: setSoftwarePercentage, color: '#795548' },
                        { label: 'Insurance', value: insurancePercentage, setter: setInsurancePercentage, color: '#f44336' }
                      ].map((item, index) => {
                         const actualPercentage = (item.value / 100) * globalOverheadPercentage;
                         
                         return (
                           <Box key={index} sx={{ 
                             p: 2,
                             borderRadius: 3,
                             border: `1px solid ${theme.palette.grey[400]}`,
                             background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                             backdropFilter: 'blur(10px)',
                             transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                             '&:hover': {
                               transform: 'translateY(-2px)',
                               boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                             }
                           }}>
                             <Grid container spacing={2} alignItems="center">
                               <Grid item xs={3}>
                                 <Typography variant="body2" sx={{ 
                                   fontWeight: 500, 
                                   color: item.color
                                 }}>
                                   {item.label}
                                 </Typography>
                                 <Typography variant="caption" color="text.secondary">
                                   = {actualPercentage.toFixed(1)}% total
                                 </Typography>
                               </Grid>
                               
                               <Grid item xs={1}>
                                 <Typography variant="h6" sx={{ 
                                   fontWeight: 700, 
                                   color: item.color,
                                   textAlign: 'center'
                                 }}>
                                   {item.value}%
                                 </Typography>
                               </Grid>
                               
                               <Grid item xs={8}>
                                 <Slider
                                   value={item.value}
                                   onChange={(e, newValue) => {
                                     if (!canEditPage('batch_costs')) return;
                                     // Ensure total doesn't exceed 100%
                                     const otherTotal = distributionTotal - item.value;
                                     const maxValue = Math.min(100 - otherTotal, newValue);
                                     item.setter(Math.max(0, maxValue));
                                   }}
                                   step={1}
                                   min={0}
                                   max={100}
                                   size="small"
                                   disabled={!canEditPage('batch_costs')}
                                   sx={{
                                     '& .MuiSlider-thumb': {
                                       backgroundColor: canEditPage('batch_costs') ? item.color : theme.palette.grey[400]
                                     },
                                     '& .MuiSlider-track': {
                                       backgroundColor: canEditPage('batch_costs') ? item.color : theme.palette.grey[400]
                                     },
                                     '&.Mui-disabled': {
                                       '& .MuiSlider-thumb': {
                                         backgroundColor: theme.palette.grey[400]
                                       },
                                       '& .MuiSlider-track': {
                                         backgroundColor: theme.palette.grey[400]
                                       }
                                     }
                                   }}
                                 />
                               </Grid>
                             </Grid>
                           </Box>
                         );
                       })}
                    </Stack>
                  </AccordionDetails>
                </Accordion>

                {/* Additional Costs */}
                <Accordion 
                  expanded={accordionStates.additionalCosts}
                  onChange={(event, expanded) => updateAccordionState('additionalCosts', expanded)}
                  elevation={0}
                  sx={{
                    borderRadius: '12px !important',
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    backdropFilter: 'blur(20px)',
                    overflow: 'hidden',
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                    '&:before': {
                      display: 'none'
                    },
                    '&.Mui-expanded': {
                      margin: '8px 0 8px 0',
                      borderRadius: '12px !important'
                    },
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '& .MuiAccordion-region': {
                      borderRadius: '0 0 12px 12px !important'
                    }
                  }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                    sx={{
                      background: 'white',
                      backdropFilter: 'blur(10px)',
                      minHeight: 56,
                      '& .MuiAccordionSummary-content': { alignItems: 'center', my: 2 },
                      '&:hover': {
                        background: alpha(theme.palette.primary.main, 0.04)
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                      <SupplierIcon sx={{ color: theme.palette.primary.main }} />
                      <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                        Additional Costs
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                        {formatCurrency(costs.additionalCost)}
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                      {[
                        { label: 'Shipping', value: shippingCost, setter: setShippingCost },
                        { label: 'Waste', value: wasteCost, setter: setWasteCost },
                        { label: 'Rework', value: reworkCost, setter: setReworkCost },
                        { label: 'Misc', value: miscCost, setter: setMiscCost }
                      ].map((item, index) => (
                        <Grid item xs={6} key={index}>
                          <TextField
                            fullWidth
                            size="small"
                            label={item.label}
                            type="number"
                            value={item.value}
                            onChange={(e) => canEditPage('batch_costs') && item.setter(Math.max(parseFloat(e.target.value) || 0, 0))}
                            disabled={!canEditPage('batch_costs')}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': { 
                                  borderColor: canEditPage('batch_costs') ? alpha(theme.palette.grey[400], 0.8) : theme.palette.grey[300], 
                                  borderWidth: 1,
                                  borderRadius: 2
                                },
                                '&:hover fieldset': { 
                                  borderColor: canEditPage('batch_costs') ? alpha(theme.palette.primary.main, 0.4) : theme.palette.grey[300], 
                                  borderWidth: 1 
                                },
                                '&.Mui-focused fieldset': { 
                                  borderColor: canEditPage('batch_costs') ? theme.palette.primary.main : theme.palette.grey[300], 
                                  borderWidth: 1 
                                },
                                '&.Mui-disabled': {
                                  '& fieldset': {
                                    borderColor: theme.palette.grey[300]
                                  }
                                }
                              }
                            }}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Panel C: Cost Breakdown & Analysis */}
        <Grid item xs={12} lg={3}>
          <Stack spacing={3}>
            {/* Cost Breakdown */}
            <Card sx={{ 
              borderRadius: 4,
              border: `1px solid ${theme.palette.grey[400]}`,
              background: `white`,
              backdropFilter: 'blur(20px)',
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 2 }}>
                  Cost Breakdown
                </Typography>
                
                <Stack spacing={2}>
                  <Box sx={{ 
                    p: 1.5,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                    }
                  }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        Ingredients
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#000000' }}>
                        {formatCurrency(costs.totalIngredientCost)}
                      </Typography>
                    </Stack>
                  </Box>

                  <Box sx={{ 
                    p: 1.5,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                    }
                  }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        Labor ({laborPercentage}%)
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatCurrency(costs.laborCost)}
                      </Typography>
                    </Stack>
                  </Box>

                  <Box sx={{ 
                    p: 1.5,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                    }
                  }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        Overhead ({globalOverheadPercentage.toFixed(1)}%)
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatCurrency(costs.overheadCost)}
                      </Typography>
                    </Stack>
                  </Box>

                  {costs.additionalCost > 0 && (
                    <Box sx={{ 
                      p: 1.5,
                      borderRadius: 3,
                      border: `1px solid ${theme.palette.grey[400]}`,
                      background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                      }
                    }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" sx={{ fontWeight: 500 }}>
                          Additional
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatCurrency(costs.additionalCost)}
                        </Typography>
                      </Stack>
                    </Box>
                  )}

                  <Divider />

                  <Box sx={{ 
                    p: 1.5,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                    }
                  }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Total
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500, color: '#2e7d32' }}>
                        {formatCurrency(costs.totalCost)}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            {/* Ingredient Costs */}
            {costs.ingredientCosts.length > 0 && (
              <Card sx={{ 
                borderRadius: 4,
                border: `1px solid ${theme.palette.grey[400]}`,
                background: `white`,
                backdropFilter: 'blur(20px)',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 2 }}>
                    Ingredient Breakdown
                  </Typography>
                  
                  <Stack spacing={1}>
                    {costs.ingredientCosts
                      .sort((a, b) => b.total_cost - a.total_cost)
                      .map((item, index) => (
                        <Box key={item.ingredient_id} sx={{ 
                          p: 1.5,
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                          backdropFilter: 'blur(10px)',
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                          }
                        }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>
                                {item.ingredient_name}
                              </Typography>
                              <Box>
                                {(() => {
                                  const sourceIngredient = ingredients.length > 0 ? 
                                    ingredients.find(ing => ing.raw_material_id === item.ingredient_id) :
                                    formulationIngredients.find(ing => ing.ingredient_id === item.ingredient_id);
                                  
                                  const rawMaterial = sourceIngredient?.raw_materials || 
                                    rawMaterials.find(rm => rm.id === item.ingredient_id);
                                  
                                  const rawMaterialConversion = calculateRawMaterialAmountNeeded(
                                    item.amount,
                                    item.unit,
                                    rawMaterial
                                  );
                                  
                                  // Check if item.amount is already in raw material units
                                  const isAlreadyInRawUnits = item.unit === rawMaterial?.primary_unit_measure;
                                  
                                  if (isAlreadyInRawUnits && rawMaterial?.primary_unit_measure) {
                                    // Amount is already in raw material units
                                    const formulation = formulations.find(f => f.id === batch?.formulation_id);
                                    const formulationUnit = formulation?.unit_of_measure || 'kg';
                                    
                                    // Only show conversion if raw material unit is different from formulation unit
                                    if (item.unit !== formulationUnit) {
                                      try {
                                        const formulationEquivalent = convertUnits(
                                          item.amount,
                                          item.unit,
                                          formulationUnit,
                                          rawMaterial.density_g_ml || 1.0
                                        );
                                        return (
                                          <Typography variant="caption" color="text.secondary">
                                            {item.percentage?.toFixed(1)}% • {item.amount?.toFixed(3)} {item.unit} (≈{formulationEquivalent.toFixed(3)} {formulationUnit})
                                          </Typography>
                                        );
                                      } catch (error) {
                                        return (
                                          <Typography variant="caption" color="text.secondary">
                                            {item.percentage?.toFixed(1)}% • {item.amount?.toFixed(3)} {item.unit}
                                          </Typography>
                                        );
                                      }
                                    } else {
                                      // Same units - no conversion needed
                                      return (
                                        <Typography variant="caption" color="text.secondary">
                                          {item.percentage?.toFixed(1)}% • {item.amount?.toFixed(3)} {item.unit}
                                        </Typography>
                                      );
                                    }
                                  } else if (rawMaterialConversion) {
                                      return (
                                        <Typography variant="caption" color="text.secondary">
                                          {item.percentage?.toFixed(1)}% • {rawMaterialConversion.amount} {rawMaterialConversion.unit} (≈{item.amount?.toFixed(3)} {item.unit})
                                        </Typography>
                                      );
                                  } else {
                                    return (
                                      <Typography variant="caption" color="text.secondary">
                                        {item.percentage?.toFixed(1)}% • {item.amount?.toFixed(3)} {item.unit}
                                      </Typography>
                                    );
                                  }
                                })()}
                              </Box>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography variant="caption" sx={{ 
                                fontWeight: 500,
                                color: item.has_pricing ? '#000000' : '#ff9800'
                              }}>
                                {item.has_pricing ? formatCurrency(item.total_cost) : 'No Price'}
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>
                      ))}
                    
                    <Divider sx={{ my: 1 }} />
                    
                    <Box sx={{ 
                      p: 1.5,
                      borderRadius: 3,
                      border: `1px solid ${theme.palette.grey[400]}`,
                      background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                      }
                    }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Total Ingredients
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#2e7d32' }}>
                          {formatCurrency(costs.totalIngredientCost)}
                        </Typography>
                      </Stack>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Batch Info */}
            <Card sx={{ 
              borderRadius: 4,
              border: `1px solid ${theme.palette.grey[400]}`,
              background: `white`,
              backdropFilter: 'blur(20px)',
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 2 }}>
                  Batch Info
                </Typography>
                
                <Stack spacing={1}>
                  <Box sx={{ 
                    p: 1.5,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                    }
                  }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        Batch Size
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        {(() => {
                          const actualVolume = calculateActualProductionVolume();
                          return actualVolume ? 
                            `${actualVolume.volume.toFixed(3)} ${actualVolume.unit}` :
                            `${batch.planned_quantity} ${batch.planned_unit_of_measure}`;
                        })()}
                      </Typography>
                    </Stack>
                  </Box>

                  <Box sx={{ 
                    p: 1.5,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                    }
                  }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        Ingredients
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        {costs.ingredientCosts.length}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BatchCosts; 